import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Factory, Plus, Edit2, Trash2, Loader2, Globe, Hash } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';

const API_URL = getApiBaseUrl();

const emptyForm = {
  factory_name: '',
  factory_code: '',
  country: '',
  dispatch_address: '',
  is_active: true,
};

export default function FactoriesPage() {
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFactory, setEditingFactory] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchFactories();
  }, []);

  const factoryCodes = useMemo(
    () => new Set(factories.map((item) => (item.factory_code || '').toUpperCase())),
    [factories]
  );

  const fetchFactories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/factories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFactories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching factories:', error);
      toast.error('Failed to load factories');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingFactory(null);
    setFormData({ ...emptyForm });
    setIsModalOpen(true);
  };

  const openEditModal = (factory) => {
    setEditingFactory(factory);
    setFormData({
      factory_name: factory.factory_name || '',
      factory_code: factory.factory_code || '',
      country: factory.country || '',
      dispatch_address: factory.dispatch_address || '',
      is_active: factory.is_active !== false,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingFactory(null);
    setFormData({ ...emptyForm });
  };

  const validateFactoryCode = () => {
    const normalized = (formData.factory_code || '').trim().toUpperCase();
    if (!normalized) {
      toast.error('Factory code is required');
      return false;
    }

    const isSameAsCurrent =
      editingFactory && normalized === (editingFactory.factory_code || '').toUpperCase();
    if (!isSameAsCurrent && factoryCodes.has(normalized)) {
      toast.error(`Factory code '${normalized}' already exists`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFactoryCode()) return;

    setSaving(true);
    const token = localStorage.getItem('token');
    const payload = {
      factory_name: (formData.factory_name || '').trim(),
      factory_code: (formData.factory_code || '').trim().toUpperCase(),
      country: (formData.country || '').trim() || null,
      dispatch_address: (formData.dispatch_address || '').trim() || null,
      is_active: !!formData.is_active,
    };

    try {
      if (editingFactory) {
        await axios.put(`${API_URL}/api/factories/${editingFactory.factory_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Factory updated successfully');
      } else {
        await axios.post(`${API_URL}/api/factories`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Factory created successfully');
      }
      closeModal();
      fetchFactories();
    } catch (error) {
      console.error('Error saving factory:', error);
      const message = error?.response?.data?.detail || 'Failed to save factory';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFactory = async (factory) => {
    const ok = window.confirm(`Delete factory '${factory.factory_name}'? This action cannot be undone.`);
    if (!ok) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/factories/${factory.factory_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Factory deleted successfully');
      fetchFactories();
    } catch (error) {
      console.error('Error deleting factory:', error);
      const message = error?.response?.data?.detail || 'Failed to delete factory';
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="factories-page-loading-state">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="factories-page-root">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2" data-testid="factories-page-title">
            <Factory className="w-8 h-8" />
            Factory Management
          </h1>
          <p className="text-slate-600 mt-1" data-testid="factories-page-subtitle">
            Manage production factories and dispatch destinations
          </p>
        </div>
        <Button onClick={openCreateModal} size="lg" data-testid="factories-add-factory-button">
          <Plus className="w-5 h-5 mr-2" />
          Add Factory
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle data-testid="factories-table-title">All Factories ({factories.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {factories.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="factories-empty-state">
              <Factory className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>No factories configured yet</p>
              <Button onClick={openCreateModal} className="mt-4" data-testid="factories-empty-state-add-button">
                Add Your First Factory
              </Button>
            </div>
          ) : (
            <Table data-testid="factories-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Factory Name</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Dispatch Address</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {factories.map((factory) => (
                  <TableRow key={factory.factory_id} data-testid={`factory-row-${factory.factory_id}`}>
                    <TableCell className="font-mono font-semibold text-blue-700">
                      <div className="flex items-center gap-1">
                        <Hash className="w-4 h-4" />
                        {factory.factory_code}
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold">{factory.factory_name}</TableCell>
                    <TableCell className="text-slate-600">
                      <div className="flex items-center gap-1">
                        <Globe className="w-4 h-4" />
                        {factory.country || '—'}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 max-w-[360px] truncate" title={factory.dispatch_address || ''}>
                      {factory.dispatch_address || '—'}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          factory.is_active !== false
                            ? 'bg-green-100 text-green-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                        data-testid={`factory-status-badge-${factory.factory_id}`}
                      >
                        {factory.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(factory)}
                          data-testid={`factory-edit-button-${factory.factory_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteFactory(factory)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`factory-delete-button-${factory.factory_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl" data-testid="factory-modal">
          <DialogHeader>
            <DialogTitle data-testid="factory-modal-title">
              {editingFactory ? 'Edit Factory' : 'Add New Factory'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 py-4" data-testid="factory-form">
            <div>
              <Label htmlFor="factory_name">Factory Name *</Label>
              <Input
                id="factory_name"
                required
                value={formData.factory_name}
                onChange={(e) => setFormData({ ...formData, factory_name: e.target.value })}
                placeholder="India In-House"
                data-testid="factory-name-input"
              />
            </div>

            <div>
              <Label htmlFor="factory_code">Factory Code *</Label>
              <Input
                id="factory_code"
                required
                value={formData.factory_code}
                onChange={(e) => setFormData({ ...formData, factory_code: e.target.value.toUpperCase() })}
                placeholder="IND-A"
                className="font-mono"
                data-testid="factory-code-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="India"
                  data-testid="factory-country-input"
                />
              </div>
              <div className="flex items-center gap-2 pt-7">
                <Checkbox
                  id="is_active"
                  checked={!!formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: !!checked })
                  }
                  data-testid="factory-active-checkbox"
                />
                <Label htmlFor="is_active" className="cursor-pointer">Factory is active</Label>
              </div>
            </div>

            <div>
              <Label htmlFor="dispatch_address">Dispatch Address</Label>
              <Input
                id="dispatch_address"
                value={formData.dispatch_address}
                onChange={(e) => setFormData({ ...formData, dispatch_address: e.target.value })}
                placeholder="Unit 7, Industrial Park, Mumbai"
                data-testid="factory-dispatch-address-input"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                disabled={saving}
                data-testid="factory-modal-cancel-button"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="factory-modal-save-button">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingFactory ? (
                  'Update Factory'
                ) : (
                  'Create Factory'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
